import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ViewStateActions } from '@common/store/navigation/actions/viewstate.actions';
import { selectCurrentViewState, selectViewStateFilters } from '@common/store/navigation/selectors/viewstate.selectors';
import { DiscoverSearchService } from '@discover/services/discover-search-service';
import { DiscoverService } from '@discover/services/discover-service';
import { WireActions, WireApiActions } from '@discover/store/actions/discover.actions';
import { selectTreeControl } from '@discover/store/selectors/discover.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { NodeType, TreeLikeFlatNode } from '@src/suite/common/data-models/search-results/search-results';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { navigateTo } from '@src/suite/common/utils/store-utils';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { from, of, timer } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Injectable()
export class DiscoverEffects {
    private actions$: Actions = inject(Actions);
    private discoverSearchService: DiscoverSearchService = inject(DiscoverSearchService);
    private discoverService: DiscoverService = inject(DiscoverService);
    private dialog: MatDialog = inject(MatDialog);
    private store: Store = inject(Store);
    private viewStateService: ViewStateService = inject(ViewStateService);
    private playbookSearchService: PlaybookSearchService = inject(PlaybookSearchService);

    startPolling$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.startPolling),
            concatLatestFrom(() => [this.store.select(selectCurrentViewState), this.store.select(selectViewStateFilters)]),
            switchMap(([action, vs, filters]) => {
                return timer(0, action.interval).pipe(
                    switchMap(() => {
                        if (vs.PSTViewStateType === ViewStateTypes.WIRESEARCH) {
                            return this.discoverSearchService.searchWires(vs as StorytellingWireSearchViewStateRecord, filters).pipe(
                                map(res => WireApiActions.loadWiresSuccess({ wires: res.results as StorytellingWireRecord[] })),
                                catchError(error => of(WireApiActions.loadWiresError({ error })))
                            );
                        } else {
                            return this.discoverSearchService.loadWires(vs, filters).pipe(
                                map(data => WireApiActions.loadWiresSuccess({ wires: data })),
                                catchError(error => of(WireApiActions.loadWiresError({ error })))
                            );
                        }
                    }),
                    takeUntil(this.actions$.pipe(ofType(WireApiActions.stopPolling)))
                );
            })
        );
    });

    stopPolling$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.stopPolling),
            switchMap(action => {
                if (action.restart) {
                    return of(WireApiActions.startPolling({ interval: action.interval }));
                } else {
                    return of(ApplicationActions.noOpAction());
                }
            })
        );
    });

    searchWires$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.searchWires),
            concatLatestFrom(() => this.store.select(selectViewStateFilters)),
            switchMap(([action, filters]) => {
                return this.discoverSearchService.searchWires(action.vs, filters).pipe(
                    takeUntil(this.actions$.pipe(ofType(ViewStateActions.setCurrentViewStateSuccess))),
                    map(res => WireApiActions.loadWiresSuccess({ wires: res.results as StorytellingWireRecord[] })),
                    catchError(error => of(WireApiActions.loadWiresError({ error })))
                );
            })
        );
    });

    navigateToWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.navigateToWire),
            concatLatestFrom(() => this.store.select(selectTreeControl)),
            map(([action, treecontrol]) => {
                const candidates: {
                    item: TreeLikeFlatNode;
                    index: number;
                }[] = [];
                let filteredIndex = 0;
                //We need that loop to match the indexed with the records when we remove unnecessary nodes
                for (const item of treecontrol.dataNodes) {
                    if (item.nodeType === NodeType.RECORD) {
                        candidates.push({ item, index: filteredIndex++ });
                    }
                }
                const nextItem = navigateTo(action.direction, action.currentNodeUuid, candidates);
                return ListActions.navigateObject({ nodeUuid: nextItem.uniqueId });
            })
        );
    });

    createWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.createTopic),
            map(action => {
                return OpenFullscreenModalActions.initCreateNewRecordFullscreen({
                    recordType: ObjectTypes.TOPIC,
                    wireRecord: action.wire,
                });
            })
        );
    });

    openNewTopicFromWire$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireActions.openNewTopicFromWire),
            exhaustMap(data => {
                this.dialog.closeAll();
                return this.dialog.afterAllClosed.pipe(
                    take(1),
                    map(() => {
                        //TODO remove topicRecord argument from this action when we decide what we want to do when creating a new topic from a wire
                        return OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: data.topic?.Guid, recordType: data.topic?.Type, topicRecord: data.topic });
                    })
                );
            })
        );
    });

    createItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.createDiscoverItem),
            switchMap(action => {
                return from(this.discoverService.createDiscoverItem(action.wire)).pipe(
                    map(res => {
                        if (res.status) {
                            return PlaybookApiActions.createTopic({
                                topic: action.topic,
                                createAndNext: action.createAndNext,
                                discoverItemDatabase: res.data.Database,
                                discoverItemPath: res.data.Path,
                                discoverItemGuid: res.data.Guid,
                            });
                        }
                    }),
                    catchError(error => of(WireApiActions.createDiscoverItemError({ error })))
                );
            })
        );
    });

    attachItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WireApiActions.attachItem),
            switchMap(action => {
                return from(this.discoverService.attachDiscoverItem(action.wire, action.topic));
            }),
            map(() => {
                this.dialog.closeAll();
                //todo move to records effects
                return WireApiActions.attachItemSuccess();
            })
        );
    });
}
